/**
 * 파일명 : hom0201p.tsx
 * 설명 : 프로젝트매핑 탭 사업장매핑 등록 화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2023-04-07   이경원 외 2명     최초작성
 * 1.1       2023-05-12   장용, 황은택
 * 1.2       2024-03-13   이경원            프로젝트등록의 고객사명 등록창에 파라미터 넘겨지도록 수정 
 * 1.3       2024-03-19   이경원            조회조건 Enter키 이벤트 추가 
 */

/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import React from 'react';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Hom0201pAction } from '../../../redux/slice/hom/hom0201p_slice';
import { IBusinessState, IInquiryCndsState } from '../../../redux/models/hom/hom0201p_model';

/* AntD */
import { Button, Space, Input, Row, Col, Layout, Typography, Collapse, Table } from 'antd';
import { SearchOutlined, CaretRightOutlined } from '@ant-design/icons';

/* 업무 */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';

const Hom0201p = (props:any) => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
    const dispatch = useAppDispatch();
    const current      = useAppSelector((state) => state.Hom0201p.current);
    const pageSize     = useAppSelector((state) => state.Hom0201p.pageSize);
    const inquiryCnds  = useAppSelector((state) => state.Hom0201p.inquiryCnds);
    const businessList = useAppSelector((state) => state.Hom0201p.businessList);

    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
    React.useEffect(() => {
        onClickSearch();

        return () => {
            dispatch(Hom0201pAction.initState());
        }
    }, []);


    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Text } = Typography;
    const { Panel } = Collapse;
   
    /* Table Columns */
    const columns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>기관코드</div>,
                dataIndex: 'companyInstCd',
                key: 'companyInstCd',
                align: 'center',
                width: '20%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>사업장명</div>,
                dataIndex: 'instNm',
                key: 'instNm',
                align: 'center',
                width: '50%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>{props.trDsc === '02' ? '선택' : '매핑'}</div>,
                key: 'action',
                render: (record:IBusinessState) => (
                    <Button
                        id='btnSave'
                        name='btnSave'
                        className='background-color-default-button'
                        size='small'
                        onClick={(e) => onClick(e, record)}
                    >
                        {props.trDsc === '02' ? '선택' : '매핑'}
                    </Button>
                ),
                align: 'center',
                width: '15%',
            }];
            return arr;
    }, []);

    /**
     * 기능 : 저장버튼 클릭 이벤트
     * 설명 : 등록/수정
     * param : 
     * return : None
     */
    const btnSave_onClick = (params:IBusinessState) => {
        // 프로젝트 등록창의 고객사명 검색으로 들어온 경우 
        if (props.trDsc==='02') {
            props.accept(params);
           
        } else {
            /* 변수선언 및 셋팅 영역 */
            // 입력파라미터
            const requestData = { companyId : params.companyId
                                , companyInstCd : params.companyInstCd
                                , prjId : props.id
                                , prjMappingId : props.prjMappingId
                                , useYn : 'Y'
                                };    // 입력파라미터

            /* 트렌젝션 호출 및 결과 셋팅 */
            commonProxy.callAsyncSvc(requestData, {
                txCd: 'TRHOMS0201',
                success: (_:any) => {
                    const param = {
                        title : '위드정보 확인',
                        contents: '정상적으로 처리되었습니다.',
                        accept: () => props.accept(params),
                    };
                    common.openAlert(param);
                },
            });
        }
    };

    /**
     * 기능 : 초기화 이벤트 선언
     * param : 
     */
    const fn_Reset = () => {
        dispatch(Hom0201pAction.initState());
    };

    /**
     * 기능 : 결과초기화 이벤트 선언
     * param : 
     */
    const fn_ResetResult = () => {
        dispatch(Hom0201pAction.setBusinessList([]));
    };

    /**
     * 기능 : 화면 닫기
     * 설명 : 화면 닫기
     * param : 
     * return : None
     */
    const btnClose_onClick = () => {
        props.cancel();
    }

    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e:any, record:IBusinessState) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btn_Search': {
                fn_ResetResult();
                onClickSearch();
                break;
            }
            
            case 'btnSave': {
                const params:IBusinessState = { companyId : record.id, companyInstCd : record.companyInstCd, cusNm : record.instNm  };
                btnSave_onClick(params);
                break;
            }

            case 'btn_Reset': {
                fn_Reset();
                break;
            }

            case 'btnClose': {
                btnClose_onClick();
                break;
            }

            default:
        }
    };

    /**
     * 기능 : 검색조회-검색어를 입력 후 조회를 눌렀을 때 실행
     * param : 
     */
    const onClickSearch = () => {
        dispatch(Hom0201pAction.setCurrent(1));
        // 입력파라미터
        const requestData:IInquiryCndsState = {
            ...inquiryCnds
        };    // 입력파라미터

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRHOMR0101',
            success: (responseData:any) => {
                dispatch(Hom0201pAction.setBusinessList(responseData.businessItems));
            },
        });
    };

    /**
     * 기능 : 검색조회-Enter키를 눌렀을 때 실행
     * param : 
     */
    const handleKeyDown = (event:any) => {
        if (event.key === 'Enter') {
          // 엔터 키가 눌렸을 때 실행할 동작
          fn_ResetResult();
          onClickSearch();
        }
    };


    return (
        <>
            <Layout>
                <Content>
                    <Collapse
                        bordered={false}
                        defaultActiveKey={['1']}
                        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                        className='site-collapse-custom-collapse'
                    >
                        <Panel header='검색조건' key='1' className='site-collapse-custom-panel'>
                            <Row
                                align='middle'
                                justify='space-between'
                                style={{ marginTop: 10 }}
                            >
                                <Col flex='9.5'>
                                    <Space>
                                        <label>사업장명</label>
                                        <Input
                                            placeholder='입력하세요'
                                            style={{ width: 180 }}
                                            value={inquiryCnds.instNm}
                                            onChange={(e:any) => {
                                                dispatch(Hom0201pAction.setInquiryCnds({ ...inquiryCnds, instNm: e.currentTarget.value }));
                                            }}
                                            onKeyDown={handleKeyDown}
                                        />
                                    </Space>
                                </Col>
                                <Col flex='0.5'>
                                    <Space>
                                        <Button
                                            name='btn_Reset'
                                            className='background-color-default-button'
                                            onClick={(e:any) => onClick(e, null)}
                                        >
                                            초기화
                                        </Button>
                                        <Button
                                            name='btn_Search'
                                            className='background-color-primary-button'
                                            icon={<SearchOutlined />}
                                            onClick={(e:any) => onClick(e, null)}
                                        >
                                            조회
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Panel>
                    </Collapse>
                    <Row align='middle' style={{ height: 50 }}>
                        <Col>
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text strong>사업장 목록</Text>
                            </Space>
                        </Col>
                        <Col flex='auto' style={{ marginRight: 15 }}>
                        </Col>
                    </Row>
                    <Table dataSource={businessList}
                        key='id'
                        columns={columns()}
                        size='small'
                        style={{width:'100%'}}
                        scroll={{y:550}}
                        pagination={{
                            pageSize: pageSize,
                            current: current,
                            showTotal : () => '총 ' + businessList.length + ' 건',
                            onChange: (page, pageSize) => {
                                dispatch(Hom0201pAction.setCurrent(page));
                                dispatch(Hom0201pAction.setPageSize(pageSize));
                            },
                        }}
                    />
                    <Row justify='center'>
                        <Col>
                            <Button
                                name='btnClose'
                                className='background-color-default-button'
                                onClick={(e:any) => onClick(e, null)}
                            >
                                닫기
                            </Button>
                        </Col>
                    </Row>
                </Content>
            </Layout>
        </>
    );
};

export default Hom0201p;